import type { Dispatch, ReactElement, ReactNode, SetStateAction } from "react"
import { Fragment, useContext } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import type { FetchNextPageOptions, InfiniteQueryObserverResult } from "react-query"
import { useSearchParams } from "react-router-dom"

import { Grid, List, ListItem, ListSubheader } from "@mui/material"
import { Skeleton } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanEmptyState } from "../../../../components/utility/KonanEmptyState"
import type { WorkflowList } from "../../../../types/generated/api/WorkflowList"
import { getEnableClose } from "../../../../utils/SessionStorageHelpers"
import { WorkflowCanvasContext } from "../../context/Context"
import type { ConfirmCloseDialogState } from "../../types/interfaces"
import { SidebarButton } from "./SidebarButton"

import styles from "../../Workflows.module.scss"

type Props = {
  setActiveWorkflow: Dispatch<SetStateAction<{ workflowName: string; workflowId: string }>>
  setIsEditMode: Dispatch<SetStateAction<boolean>>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  orderedWorkflows: any
  hasMore: boolean
  shouldFetchNext: boolean
  workflowsLength: number
  searchValue: string
  fetchNext: (
    options?: FetchNextPageOptions,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ) => Promise<InfiniteQueryObserverResult<AxiosResponse<any, any>, AxiosError<unknown, any>>>
  actions: ReactNode
  setConfirmCloseDialogOpen: Dispatch<SetStateAction<ConfirmCloseDialogState>>
}

/**
 * WorkflowSidebar component that displays a list of workflows with infinite scroll functionality.
 * Handles workflow selection, loading states, and provides actions for workflow management.
 */
export function WorkflowSidebar(props: Readonly<Props>): ReactElement {
  // Prop destructuring
  const {
    setActiveWorkflow,
    setIsEditMode,
    orderedWorkflows,
    hasMore,
    fetchNext,
    shouldFetchNext,
    actions,
    searchValue,
    setConfirmCloseDialogOpen,
  } = props

  // Context hooks
  const { workflow, formik, isWorkflowsLoading } = useContext(WorkflowCanvasContext)

  // Navigation hooks
  const [searchParams] = useSearchParams()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSidebar",
  })

  // Constants
  const activeWorkflowId = workflow?.uuid

  // Intersection observer hook
  const { ref: lastWorkflowItemRef } = useInView({
    onChange: (inView: boolean) => {
      inView && hasMore && shouldFetchNext && fetchNext()
    },
  })

  // Helper functions
  const handleSelectWorkflow = (name: string, uuid: string): void => {
    if (uuid !== activeWorkflowId) {
      if (activeWorkflowId === "new" || getEnableClose()) {
        setConfirmCloseDialogOpen({ isOpen: true, action: { name: "switch", workflowName: name, workflowId: uuid } })
      } else {
        setActiveWorkflow({ workflowName: name, workflowId: uuid })
        setIsEditMode(false)
        formik.values.workflowName = ""
      }
    }
  }

  // Skeleton component for loading states
  const SkeletonItems = (): ReactElement => (
    <Grid container item xs={12} pl={1.5} pr={1.25} gap={1.5} mb={1}>
      {[1, 2, 3].map((item) => {
        return (
          <Grid item xs={12} key={item}>
            <Skeleton variant="rectangular" width={"100%"} height={20} />
          </Grid>
        )
      })}
    </Grid>
  )

  return (
    <Grid item container className={styles.sideBar} id="workflows-scrollable-target">
      <Grid item>
        <List sx={{ padding: "0px" }}>
          <ListSubheader className={styles.subheader}>{actions}</ListSubheader>

          {isWorkflowsLoading ? (
            <SkeletonItems />
          ) : orderedWorkflows?.length > 0 ? (
            <Fragment>
              {orderedWorkflows?.map((wf: WorkflowList, index: number) => {
                const isSelected = wf?.uuid === searchParams.get("workflowId")
                return (
                  <ListItem
                    key={wf?.uuid}
                    onClick={() => handleSelectWorkflow(wf?.name, wf?.uuid)}
                    ref={hasMore && index === orderedWorkflows.length - 1 ? lastWorkflowItemRef : undefined}
                    sx={{
                      padding: "0px",
                      bgcolor: "transparent",
                    }}
                  >
                    <SidebarButton workflow={wf} isSelected={isSelected} hasWorkflows={orderedWorkflows?.length > 0} />
                  </ListItem>
                )
              })}
              {hasMore && <SkeletonItems />}
            </Fragment>
          ) : (
            <KonanEmptyState
              title={t("emptyState.title", { searchValue: searchValue })}
              subTitle={t("emptyState.subtitle")}
            />
          )}
        </List>
      </Grid>
    </Grid>
  )
}
